import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient, DocumentNode } from '@apollo/client';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';

import { LoginInput } from '../types/Auth';

import { isAuthenticatedQuery, IsAuthenticatedQueryResult } from '../graphql/queries/isAuthenticatedQuery';
import { getUserProfileQuery, GetUserProfileQueryResult } from '../graphql/queries/getUserProfileQuery';
import { signInMutation, SignInMutationResult, SignInMutationVariables } from '../graphql/mutations/signInMutation';
import { signOutMutation } from '../graphql/mutations/signOutMutation';

export interface AuthHookInput {
  UserFragment: DocumentNode;
}

export interface AuthHookPayload<TUser> {
  isAuthenticated: boolean;
  loading: boolean;

  user: TUser | undefined;
  loadingUser: boolean;
  refetchProfile: () => void;

  onSignIn: (input: LoginInput, onSuccess?: () => void) => void;
  signInLoading: boolean;
  signInError?: string;

  onSignOut: (onSuccess?: () => void) => void;
}

export default function useAuth<TUser>(input: AuthHookInput): AuthHookPayload<TUser> {
  const { UserFragment } = input;
  const apolloClient = useApolloClient();
  const history = useHistory();

  const { data, loading, refetch: refetchAuthData } = useQuery<IsAuthenticatedQueryResult>(isAuthenticatedQuery);

  const isAuthenticated = data?.authData?.isAuthenticated || false;

  const {
    data: userProfileData,
    loading: loadingUser,
    refetch: refetchProfile,
  } = useQuery<GetUserProfileQueryResult<TUser>>(getUserProfileQuery(UserFragment), {
    skip: loading || !isAuthenticated,
  });

  const handleAuthCompleted = useCallback(async () => {
    // Flip the auth flag synchronously — its cache key is stable
    // (`IsAuthenticatedPayload:static-id`, see initApolloClient) so this
    // write reliably broadcasts to subscribers, un-skipping the profile
    // query and the rest of the app.
    apolloClient.cache.writeQuery({
      query: isAuthenticatedQuery,
      data: {
        authData: {
          __typename: 'IsAuthenticatedPayload',
          isAuthenticated: true,
        },
      },
    });
    // Force a network fetch for the profile rather than re-using
    // `writeQuery(getUserProfileQuery)`: apollo-link-rest does not
    // reliably re-broadcast a manual writeQuery against a @rest-decorated
    // query to active useQuery subscribers, which left the SPA with no
    // user data (and dependent feature-flag queries empty) until a
    // page reload. Going through `apolloClient.query` populates the
    // cache via Apollo's normal pipeline so everything reacts.
    await apolloClient.query({
      query: getUserProfileQuery(UserFragment),
      fetchPolicy: 'network-only',
    });
  }, [apolloClient, UserFragment]);

  /**
   * Sign in
   */
  const [signIn, { loading: signInLoading, error: signInError }] = useMutation<SignInMutationResult<TUser>, SignInMutationVariables>(
    signInMutation(UserFragment),
    {
      onError: () => null,
    }
  );
  const onSignIn = useCallback(
    (input: LoginInput, onSuccess?: () => void) => {
      signIn({ variables: { input } }).then(async ({ data }) => {
        if (data?.user) {
          await handleAuthCompleted();
          if (onSuccess) {
            onSuccess?.();
          }
        }
      });
    },
    [signIn, handleAuthCompleted]
  );

  /**
   * Sign out
   */
  const [signOut] = useMutation(signOutMutation);
  const onSignOut = useCallback(
    (onSuccess?: () => void) => {
      const cleanSession = async () => {
        await apolloClient.clearStore();
        await refetchAuthData();
      };
      signOut().finally(() => {
        cleanSession().finally(() => {
          onSuccess?.();
          history.push('/login');
        });
      });
    },
    [signOut, apolloClient, refetchAuthData, history]
  );

  return {
    isAuthenticated,
    loading,

    user: userProfileData?.user,
    loadingUser,
    refetchProfile,

    signInLoading,
    signInError: getErrorMessage({ error: signInError }),
    onSignIn,

    onSignOut,
  };
}
